import type { CSSProperties } from "react";

/**
 * Die Hülle von Shell 2.0: DREI SCHALEN plus zwei Attribute.
 *
 * ── WAS DER BETREIBER ENTSCHIEDEN HAT (2026-09-18) ──────────────────────────
 *
 * „Die Design-Kits übernehmen wir so nicht. Wir bieten DREI Shells an: die
 * flache, die schwebende und die umklammernde. Die umklammernde ist die neue
 * Standard-Shell. Und es soll in JEDER der drei möglich sein, Milchglas zu
 * aktivieren oder zu deaktivieren."
 *
 * ── WARUM EINE SCHALE MEHRERE FELDER BÜNDELT UND KEIN REGLER JE FELD IST ────
 *
 * Bis 0.450.0 stand hier ein `Huellenrezept` mit neun Feldern, und fünf fertige
 * Zeilen davon lagen als „Design-Kits" daneben. Nebeneinandergelegt waren die
 * fünf Kits nicht fünf Entwürfe, sondern DREI Schalen mit zwei An/Aus-Schaltern:
 *
 *   Kit 1 Flach               → flach
 *   Kit 2 Schwebend           → schwebend
 *   Kit 3 Umklammert          → umklammert
 *   Kit 4 Milchglas           → flach      + milchglas
 *   Kit 5 Schwebend, geteilt  → schwebend  + kopfGetrennt
 *
 * Und die Felder des Rezepts BEDINGTEN EINANDER: `rinne` und `radius` tun bei
 * `flach` nichts, die drei Flächen sind bei `umklammert` Pflicht und bei `flach`
 * sinnlos. **Ein Feld, das nur unter einem bestimmten Wert eines anderen etwas
 * tut, ist kein eigener Regler** — es ist ein Bestandteil dessen, was es
 * bedingt. Deshalb tragen die Schalen diese Werte selbst (`SCHALEN`), und
 * gewählt wird nur noch die Schale.
 *
 * Orthogonal — also echte Attribute — sind genau zwei: ihre CSS-Regel hängt
 * NICHT an `data-jw-flaeche`, sie greift unter allen drei Schalen gleich.
 *
 * ── WARUM DAS UMSCHALTEN IN CSS PASSIERT, NICHT IN REACT ────────────────────
 *
 * `schalenStil()` gibt Datenattribute und CSS-Variablen zurück, sonst nichts.
 * Die Regeln dazu stehen in `styles/globals.css`. Das hat drei Folgen, die
 * alle drei der Grund für diese Bauweise sind:
 *
 *  1. **Ein Theme kann seine Hülle mitbringen.** Die Variablen sind gewöhnliche
 *     Custom Properties; ein Theme setzt sie wie jede andere Farbe. Genau das
 *     hat der Betreiber verlangt: „damit es über die Themes konfiguriert werden
 *     kann".
 *  2. **Kein Re-Render beim Umschalten.** Ein Attribut am Wurzelknoten kostet
 *     keinen React-Baum.
 *  3. **Die Komponenten wissen nichts davon.** `Sidebar` und `PageHeader`
 *     tragen je eine Marke (`data-jw-…`) und sonst keine Kenntnis der Schalen.
 */
export type Schale = "flach" | "schwebend" | "umklammert";

/**
 * Die Form des AKTIVEN Navigationseintrags — und derselben Form folgen die
 * Reiter (`.jw-aktiv`).
 *
 * KEIN Merkmal der Schale, sondern eine eigene Einstellung. Betreiber,
 * 2026-09-18: „ich möchte nicht, dass die Design-Kits dort andere Sachen ändern
 * — die alten Knöpfe finde ich geiler, son primärfarbener Block." Wer die Form
 * ändern will, stellt sie ein; sie fällt einem nicht mit der Schale zu.
 *
 * **Drei davon setzen Akzent-Text auf den Leisten-Grund, und dafür gibt es
 * keine Kontrast-Zusage.** `theme-contrast.test.ts` prüft die Token-Paare, die
 * die Hülle wirklich benutzt — `primary` auf `background` gehört bei `pille`
 * nicht dazu, denn dort steht `primary-foreground` auf `primary`. Wer `karte`,
 * `text` oder `balken` wählt, prüft den Kontrast am Bild.
 */
export type AktivForm = "pille" | "karte" | "getoent" | "balken" | "text";

/**
 * Die Form der unteren Leiste am Telefon (`Bodenleiste`), Betreiber 2026-10-21:
 * „klammer" liegt im Rahmen unter der Inhaltskarte — Kopf oben, Leiste unten,
 * die Karte dazwischen umklammert. „pille" und „insel" schweben ueber dem
 * Inhalt; „insel" setzt den Mehr-Knopf als eigenen Kreis daneben.
 */
export type Bodenleistenform = "klammer" | "pille" | "insel";
export const BODENLEISTEN_FORMEN: readonly Bodenleistenform[] = ["klammer", "pille", "insel"];

/**
 * Was eine Schale mitbringt: Geometrie UND ihre Flächen, in einem Stück.
 *
 * Die Flächen gehören dazu und sind nicht daneben einstellbar. Bis 0.437.1
 * waren sie ein Regler daneben, und das Ergebnis war „Milchglas gewählt, nichts
 * passiert": die Leiste war zu 62 % durchsichtig, dahinter lag einfarbiges
 * Schwarz. Der Effekt lief, das Motiv fehlte.
 */
export interface Schalenbild {
  /** Beschriftung in der Auswahl. */
  name: string;
  /** Ein Satz, woran man diese Schale erkennt. */
  satz: string;
  /** Breite der Rinne als CSS-Länge. */
  rinne: string;
  /** Eckenradius der Karten als CSS-Länge. */
  radius: string;
  /**
   * Der RAHMEN als CSS-`background`-Wert; leer = der Theme-Hintergrund.
   *
   * Seitenleiste, Ränder und — unter „umklammert" — auch das rechte Panel.
   */
  grund: string;
  /**
   * Was auf dem Rahmen lesbar ist; leer = der Bestand (`--sidebar-foreground`).
   *
   * **Eine Fläche ohne ihren Vordergrund zu setzen ist der Fehler, den man erst
   * im hellen Theme sieht.** „Umklammert" legt eine in BEIDEN Modi dunkle
   * Schale unter die Seitenleiste; deren Beschriftungen kommen aber aus den
   * `sidebar-*`-Token des Themes, und die sind im hellen Theme für eine HELLE
   * Leiste entworfen. Ohne dieses Feld stünde dort dunkle Schrift auf dunkler
   * Schale.
   */
  grundVordergrund: string;
  /**
   * Die Fläche der Inhaltskarte; leer = der Theme-Hintergrund.
   *
   * Die ZWEITE Fläche, und der Grund, warum es sie gibt — Betreiber am
   * 2026-09-16: „wir können einmal den kompletten Background von Seitenleiste
   * und diesem Rahmen bestimmen und dann einmal den Background vom
   * Content-Bereich, dass das Kontraste sind". Vorher stand die Inhaltsfläche
   * fest auf `--background`; im hellen Janeway-Theme, wo `--sidebar` und
   * `--background` denselben Wert tragen, war „umklammert" deshalb einfarbig
   * und nur an der Kante zu erkennen.
   */
  inhalt: string;
}

/** Ein Hauch abgesetzt vom Inhalt — genug, damit eine Rinne als Rinne liest,
 *  wenig genug, um in allen 43 Themes zu tragen. `color-mix` gegen
 *  `--foreground` dreht sich im dunklen Theme von selbst um. */
const GRUND_ABGESETZT =
  "color-mix(in srgb, var(--foreground) 7%, var(--background))";

export const SCHALEN: Record<Schale, Schalenbild> = {
  flach: {
    name: "Flach",
    satz:
      "Der Bestand: Seitenleiste links, Inhalt rechts, getrennt durch Haarlinien. Nichts schwebt.",
    rinne: "0px",
    radius: "0px",
    // Alle drei leer, also der Theme-Hintergrund. Das ist die Aussage dieser
    // Schale und kein fehlender Wert: sie setzt dem Theme nichts entgegen.
    grund: "",
    grundVordergrund: "",
    inhalt: "",
  },
  schwebend: {
    name: "Schwebend",
    satz:
      "Seitenleiste und Inhalt sind je eine Karte auf der Grundfläche — Rinne rundum und dazwischen.",
    // Die Zahlen sind keine Willkür, sondern von den Vorlagen abgenommen: das
    // CMS fährt `--cms-radius: 28px` mit einer Rinne in derselben Größenordnung,
    // die schwebenden Entwürfe liegen bei 12 bis 16 px. Wer sie ändert, ändert
    // einen Wert — nicht die Mechanik.
    rinne: "0.75rem",
    radius: "0.875rem",
    grund: GRUND_ABGESETZT,
    grundVordergrund: "",
    // BEWUSST LEER, also `--background`. `var(--card)` läge im dunklen
    // Janeway-Theme bei 5 % und der Grund bei 6,9 % — zwei Flächen, die sich um
    // knapp zwei Prozent unterscheiden, lesen sich als Rundungsfehler, nicht
    // als schwebende Karte. Hier trägt gerade der HELLERE Grund gegen den
    // dunkleren Inhalt.
    inhalt: "",
  },
  umklammert: {
    name: "Umklammert",
    satz:
      "Die Seitenleiste ist die Grundfläche, der Inhalt liegt als eine Karte darauf. Die Rinne läuft rundum — wie im Janeway-CMS.",
    rinne: "0.75rem",
    radius: "1.25rem",
    // `--jw-schale`, nicht `--sidebar`. Der Unterschied ist der ganze Punkt, und
    // er ist über alle 43 Themes gemessen (Spec `2026-09-16-shell-themedatei`,
    // §3): der Kontrast `sidebar` : `background` liegt im Median bei 1,044
    // (hell) bzw. 1,021 (dunkel), und **kein einziges Theme** erreicht 1,2 : 1 —
    // also nicht einmal das, was die Bibliothek für eine gerade noch sichtbare
    // Trennlinie verlangt. Im hellen Janeway- und Mono-Theme sind die beiden
    // sogar bitgleich. Wer hier auf ein Theme-Token zurückgeht, holt sich also
    // keinen schwächeren Kontrast, sondern gar keinen.
    grund: "var(--jw-schale)",
    grundVordergrund: "var(--jw-schale-vordergrund)",
    // `--jw-tafel`, nicht `--card`: im dunklen Janeway-Theme steht die Schale
    // auf rgb(0,0,0) und `--card` auf rgb(13,13,13) — fünf Prozent Abstand
    // lesen sich als Schwarz mit Rundungsfehler, nicht als zweite Fläche. Der
    // Betreiber hat es genau so gemeldet („der Contentbereich hat immer einen
    // schwarzen Background"). Die Tafel hebt nur im dunklen Modus an; hell ist
    // die Karte ohnehin weiß gegen eine dunkle Schale.
    inhalt: "var(--jw-tafel)",
  },
};

/** Die Reihenfolge in der Auswahl: vom Bestand zur neuen Vorgabe. */
export const SCHALEN_LISTE: readonly Schale[] = ["flach", "schwebend", "umklammert"];

/**
 * Die Schale, die die Suite als Standard ANBIETET — Betreiber, 2026-09-18:
 * „Die umklammernde ist die neue Standard-Shell."
 *
 * **Nicht zu verwechseln mit `HUELLE_BESTAND`.** Das hier ist die Vorgabe für
 * eine Oberfläche, die eine Schale wählen lässt (der Showroom). Die Vorgabe von
 * `AppLayout` bleibt `flach`: 23 Apps rendern diese Hülle, und keine davon darf
 * ihr Aussehen ändern, weil jemand eine Bibliothek aktualisiert. Wer umstellt,
 * setzt es an der App.
 */
export const SCHALE_VORGABE: Schale = "umklammert";

/**
 * Was eine App wählt: eine Schale, zwei Attribute, eine Knopfform.
 *
 * Alles außer der Schale ist optional und fällt auf den Bestand zurück — eine
 * App, die nur umstellen will, schreibt `huelle={{ schale: "umklammert" }}`.
 */
export interface Huellenwahl {
  schale: Schale;
  /**
   * MILCHGLAS — in jeder der drei Schalen zu haben, und das ist der Punkt.
   *
   * Bis 0.450.0 war der Effekt mechanisch an `flach` gekettet: er steckte im
   * Kit „Milchglas", und das war flach. Orthogonal ist er, weil seine Regel an
   * `[data-jw-leiste="durchscheinend"]` hängt und `data-jw-flaeche` nicht
   * erwähnt.
   *
   * **Was er unter welcher Schale leistet, ist NICHT dasselbe.** Unter `flach`
   * und `schwebend` sieht man durch die Scheibe hindurch. Unter `umklammert`
   * verwischt `backdrop-filter` eine einfarbige Fläche zu derselben Fläche —
   * dort trägt allein das MOTIV (zwei getönte Lichter hinter der Leiste,
   * `globals.css`). Deshalb redet die Beschriftung von Licht und nicht von
   * Durchsicht; wer „Milchglas" wählt und Durchsicht erwartet, hält die
   * Einstellung für kaputt.
   */
  milchglas?: boolean;
  /**
   * Inhaltskopf und Inhalt als ZWEI Karten mit der Rinne dazwischen, statt als
   * eine Fläche.
   *
   * Betreiber, 2026-09-16: „im schwebenden Theme soll's die Möglichkeit geben,
   * dass der Content Header und der Content wirklich 2 komplett getrennte
   * Bereiche sind."
   *
   * **Unter `flach` tut es nichts, und das ist Absicht** — Betreiber,
   * 2026-09-19: „bei der flachen Hülle macht kopfgetrennt gar keinen Sinn."
   * Die Regel baut zwei Karten, und eine Karte ist hier `--jw-radius` plus
   * `--jw-rinne`; unter `flach` sind beide 0, es entstünden zwei randlose
   * Rechtecke ohne Abstand. Das Attribut wird deshalb weiter durchgereicht
   * (`data-jw-kopf="getrennt"`), aber die CSS-Regel schließt `flach` aus
   * (`globals.css`). **Es ist damit nicht mehr voll orthogonal** wie
   * `milchglas` — eine Oberfläche, die es anbietet, muss den Schalter unter
   * `flach` ausblenden, sonst steht dort ein Knopf ohne Wirkung.
   *
   * **Es braucht eine Seite, die ihren Körper auch auszeichnet**
   * (`data-jw-inhaltskoerper`). Ohne die Marke gäbe es nur eine Karte, aus der
   * der Kopf herausfiele — die Regel greift deshalb per `:has()` nur, wenn der
   * Körper da ist, und die Seite sieht sonst aus wie ohne das Attribut. Das ist
   * kein Feinschliff: eine halb angewandte Regel sieht aus wie ein Fehler, und
   * niemand weiß, ob er in der Hülle oder in der Seite steckt.
   */
  kopfGetrennt?: boolean;
  /** Die Form des aktiven Eintrags. Vorgabe `pille` — der Bestand. */
  aktiv?: AktivForm;
  /** Untere Leiste am Telefon. Vorgabe `klammer`. Wirkt nur in Apps, die eine
   *  `bodenleiste` an `AppLayout` geben. */
  bodenleiste?: Bodenleistenform;
  /** Milchglas der SCHWEBENDEN Leisten. Aus: deckend in der Rahmenfarbe, wie
   *  der Kopf. Unter `klammer` ohne Wirkung — dort ist die Leiste der Rahmen. */
  bodenleisteGlas?: boolean;
}

/**
 * Die Vorgabe von `AppLayout`: der Bestand aus Labs und Prod.
 *
 * Als Modul-Konstante und nicht als Literal in der Signatur, damit sie bei
 * jedem Rendern dasselbe Objekt ist.
 */
export const HUELLE_BESTAND: Huellenwahl = { schale: "flach" };

/**
 * Der Seitenrand des Inhalts — EINMAL benannt statt dreimal abgeschrieben.
 *
 * Dieselben Werte fuehren `PageContainer` und `SettingsPage`; sie muessen
 * uebereinstimmen, damit Inhalt und Reiterleiste darueber auf derselben Kante
 * stehen. Als Konstante kann man sie zusammen aendern.
 *
 * Unten je Stufe `max(<Bestand>, var(--jw-jane-inhalt-platz, 0px))`: Platz fuer
 * den Jane-Knopf, solange er steht (`AppLayout`, `JANE_INHALT_PLATZ_VAR`).
 */
export const INHALT_RAND = "px-4 pt-4 pb-[max(calc(4rem+var(--jw-installleiste-h,0px)),var(--jw-jane-inhalt-platz,0px))] sm:px-6 md:px-8 md:pt-6 md:pb-[max(3rem,var(--jw-jane-inhalt-platz,0px))]";

/**
 * Wahl → was an den Wurzelknoten der Hülle gehört.
 *
 * Bewusst getrennt zurückgegeben statt als fertiges `props`-Objekt: `AppLayout`
 * hat an seiner Wurzel schon eigene Klassen und einen eigenen Stil, und ein
 * Spread würde die überschreiben, ohne dass es auffällt.
 */
export function schalenStil(wahl: Huellenwahl): {
  attribute: {
    "data-jw-flaeche": Schale;
    "data-jw-kopf": "verbunden" | "getrennt";
    "data-jw-leiste": "deckend" | "durchscheinend";
    "data-jw-aktiv": AktivForm;
    "data-jw-bodenleiste": Bodenleistenform;
    "data-jw-bodenleiste-glas": "an" | "aus";
  };
  stil: CSSProperties;
} {
  const bild = SCHALEN[wahl.schale];
  return {
    attribute: {
      "data-jw-flaeche": wahl.schale,
      "data-jw-kopf": wahl.kopfGetrennt ? "getrennt" : "verbunden",
      "data-jw-leiste": wahl.milchglas ? "durchscheinend" : "deckend",
      "data-jw-aktiv": wahl.aktiv ?? "pille",
      "data-jw-bodenleiste": wahl.bodenleiste ?? "klammer",
      "data-jw-bodenleiste-glas": wahl.bodenleisteGlas ? "an" : "aus",
    },
    stil: {
      // Als Custom Properties, nicht als fertige Werte: so kann ein Theme sie
      // überschreiben, und die Regeln in globals.css lesen nur die Namen.
      ["--jw-rinne" as string]: bild.rinne,
      ["--jw-radius" as string]: bild.radius,
      /* ── DIE FLAECHEN GEHOEREN HIERHER, NICHT AN `<html>` ────────────────
         Bis 0.446.0 gab diese Funktion NUR Geometrie zurueck. Die Farben
         standen in keinem Rueckgabewert — der Showroom schrieb sie in einem
         `useEffect` selbst an `document.documentElement`, und das war der
         einzige Ort im Oekosystem, der es tat.

         Die Folge war nicht Unschoenheit, sondern dass **„umklammert" in keiner
         App ankam**: `globals.css` liest `var(--jw-grund, var(--background))`,
         und ohne Setzer greift der Rueckfall. Eine App bekam die Geometrie der
         Klammer mit der Farbe des Bestands — im Janeway-Theme hell also einen
         rgb(250,250,250)-„Rahmen" um eine rgb(250,250,250)-Karte. Gemessen am
         2026-09-18.

         Am Wurzelknoten der Huelle statt an `<html>` hat drei Vorteile: es
         braucht keinen Effekt (also kein erstes Bild mit hellem Grund und kein
         Aufraeumen beim Verlassen), zwei Huellen auf einer Seite kaemen sich
         nicht ins Gehege, und die Variablen vererben genau so weit, wie die
         Huelle reicht.

         Leere Werte werden WEGGELASSEN, nicht als "" gesetzt: eine gesetzte
         leere Custom Property deckt den Rueckfall in `var(--jw-grund, …)` zu,
         und dann faerbt ein Theme, das die Schale selbst mitbringt, nichts
         mehr. Derselbe Fehler wie „eine Vorgabe in die Deklaration schreiben"
         (s. CLAUDE.md dieser Bibliothek). */
      ...(bild.grund ? { ["--jw-grund" as string]: bild.grund } : {}),
      ...(bild.grundVordergrund
        ? { ["--jw-grund-vordergrund" as string]: bild.grundVordergrund }
        : {}),
      ...(bild.inhalt ? { ["--jw-inhalt" as string]: bild.inhalt } : {}),
    } as CSSProperties,
  };
}
